import { database, TaskOverride } from "./database";
import {
  CategoryDto,
  FormDto,
  ItemDto,
  PhotoDto,
  RecordingDto,
  SectionDto,
  TaskDto,
} from "./models/dto/Item.dto";
import { MissingError } from "./models/errors/Missing.error";
import { PagedResponse } from "./models/PagedResponse";

class BackendService {
  public async getForm(formId: number): Promise<FormDto> {
    const form = await database.forms.get(formId);

    if (form === undefined) {
      throw new MissingError("Form does not exist", "FORM", formId);
    }

    return {
      ...form,
      id: form.id!,
    };
  }

  public async getCategories(templateId: number): Promise<CategoryDto[]> {
    const categories = await database.categories
      .where("templateId")
      .equals(templateId)
      .toArray();

    return categories.map((category) => ({
      ...category,
      id: category.id!,
    }));
  }

  public async getCategory(categoryId: number): Promise<CategoryDto> {
    const category = await database.categories.get(categoryId);

    if (category === undefined) {
      throw new MissingError("Category does not exist", "CATEGORY", categoryId);
    }

    return {
      ...category,
      id: category.id!,
    };
  }

  public async getSections(categoryId: number): Promise<SectionDto[]> {
    const sections = await database.sections
      .where("categoryId")
      .equals(categoryId)
      .toArray();

    return sections.map((section) => ({
      ...section,
      id: section.id!,
    }));
  }

  public async getSection(sectionId: number): Promise<SectionDto> {
    const section = await database.sections.get(sectionId);

    if (section === undefined) {
      throw new MissingError("Section does not exist", "SECTION", sectionId);
    }

    return {
      ...section,
      id: section.id!,
    };
  }

  public async getItems(sectionId: number, formId: number): Promise<ItemDto[]> {
    const items = await database.items
      .where("sectionId")
      .equals(sectionId)
      .toArray();

    if (items.length === 0) {
      return [];
    }

    const [tasks, overrides, photos, recordings] = await Promise.all([
      database.tasks
        .where("itemId")
        .anyOf(items.map((item) => item.id!))
        .toArray(),
      database.taskOverrides.where("formId").equals(formId).toArray(),
      database.photos.where("formId").equals(formId).toArray(),
      database.recordings.where("formId").equals(formId).toArray(),
    ]);

    const overrideByTaskId = new Map<number, TaskOverride>(
      overrides.map((o) => [o.taskId, o])
    );

    const photosByTaskId = new Map<number, PhotoDto[]>();
    for (const p of photos) {
      if (!photosByTaskId.has(p.taskId)) {
        photosByTaskId.set(p.taskId, []);
      }
      photosByTaskId.get(p.taskId)!.push({
        id: p.id!,
        date: p.date,
        file: p.file,
      });
    }

    const recordingsByTaskId = new Map<number, RecordingDto[]>();
    for (const r of recordings) {
      if (!recordingsByTaskId.has(r.taskId)) {
        recordingsByTaskId.set(r.taskId, []);
      }
      recordingsByTaskId.get(r.taskId)!.push({
        id: r.id!,
        date: r.date,
        blob: r.blob,
      });
    }

    const tasksByItemId = new Map<number, TaskDto[]>();

    for (const task of tasks) {
      const override = overrideByTaskId.get(task.id!);

      const dto: TaskDto = {
        id: task.id!,
        overrideId: override?.id,
        prefix: task.prefix,
        content: task.content,
        status: override?.status ?? task.status,
        photos: photosByTaskId.get(task.id!) ?? [],
        recordings: recordingsByTaskId.get(task.id!) ?? [],
      };

      if (!tasksByItemId.has(task.itemId)) {
        tasksByItemId.set(task.itemId, []);
      }
      tasksByItemId.get(task.itemId)!.push(dto);
    }

    const itemDtos: ItemDto[] = items.map((item) => ({
      ...item,
      id: item.id!,
      tasks: tasksByItemId.get(item.id!) ?? [],
    }));

    return itemDtos;
  }

  public async deleteForm(formId: number) {
    await database.transaction(
      "rw",
      [
        database.photos,
        database.recordings,
        database.taskOverrides,
        database.forms,
      ],
      async (transaction) => {
        await transaction.photos.where("formId").equals(formId).delete();
        await transaction.recordings.where("formId").equals(formId).delete();
        await transaction.taskOverrides.where("formId").equals(formId).delete();

        await transaction.forms.delete(formId);
      }
    );
  }

  public async getStorageCount() {
    const [recordingCount, photoCount] = await Promise.all([
      database.recordings.count(),
      database.photos.count(),
    ]);

    return { recordingCount, photoCount };
  }

  public async getPhotosPaged(
    offset: number,
    limit: number
  ): Promise<PagedResponse<PhotoDto & { formId: number; formName?: string }>> {
    const total = await database.photos.count();
    const data = await database.photos.offset(offset).limit(limit).toArray();

    const forms = await database.forms
      .where("id")
      .anyOf(data.map((photo) => photo.formId))
      .toArray();

    const formTitles = new Map<number, string>();
    for (const form of forms) {
      formTitles.set(form.id!, form.templateName);
    }

    return {
      offset,
      limit,
      total,
      data: data.map((photo) => ({
        ...photo,
        id: photo.id!,
        formName: formTitles.get(photo.formId),
      })),
    };
  }

  public async deletePhoto(id: number) {
    await database.photos.delete(id);
  }

  public async getRecordingsPaged(
    offset: number,
    limit: number
  ): Promise<
    PagedResponse<RecordingDto & { formId: number; formName?: string }>
  > {
    const total = await database.recordings.count();
    const data = await database.recordings
      .offset(offset)
      .limit(limit)
      .toArray();

    const forms = await database.forms
      .where("id")
      .anyOf(data.map((recording) => recording.formId))
      .toArray();

    const formTitles = new Map<number, string>();
    for (const form of forms) {
      formTitles.set(form.id!, form.templateName);
    }

    return {
      offset,
      limit,
      total,
      data: data.map((photo) => ({
        ...photo,
        id: photo.id!,
        formName: formTitles.get(photo.formId),
      })),
    };
  }

  public async deleteRecording(id: number) {
    await database.recordings.delete(id);
  }
}

export const backendService = new BackendService();
